import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import Pagination from './Pagination';
import { useTheme } from '../../context/ThemeContext';
import { FaWhatsapp } from 'react-icons/fa';
import { FiSend, FiCheck, FiTrash2, FiSearch, FiFilter, FiX } from 'react-icons/fi';



// 🔧 FUNÇÃO DE FORMATAÇÃO OTIMIZADA PARA WHATSAPP
const formatPhoneNumberForWhatsApp = (phoneNumber) => {
  if (!phoneNumber) return null;

  // 1. Remove caracteres não numéricos
  let cleaned = phoneNumber.replace(/\D/g, '');

  // 2. Remove zeros à esquerda (ex: 082... -> 82...)
  while (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }

  // 3. Verifica se tem o DDI 55
  if (cleaned.startsWith('55')) {
    // Se tiver 13 dígitos (55 + 2 + 9), ok
    // Se tiver 12 dígitos (55 + 2 + 8), ok (fixo)
    // Se tiver mais de 13, pode ter zero extra após o 55 (ex: 55082...)
    if (cleaned.length > 13 && cleaned.charAt(2) === '0') {
      return '55' + cleaned.substring(3);
    }
    return cleaned;
  }

  // 4. Se não tem 55, adiciona
  // Celular BR: 11 dígitos (DDD + 9) -> vira 13
  // Fixo BR: 10 dígitos (DDD + 8) -> vira 12
  if (cleaned.length >= 10 && cleaned.length <= 11) {
    return '55' + cleaned;
  }

  // 5. Caso residual: Retorna como está se não se encaixar (ex: internacional sem 55?)
  // Mas se for BR, força o 55
  return '55' + cleaned;
};





// 🔥 COMPONENTE DO BADGE PARA USUÁRIOS NOVOS - CORRIGIDO
const NewUserBadge = ({ userProfile }) => {
  const { isDarkMode } = useTheme();

  // Verifica diretamente pelo perfil do usuário
  const isNewUser = userProfile?.isNewUser && userProfile?.userStatus === 'pending';

  if (!isNewUser) {
    return null;
  }

  return (
    <div className="relative group">
      <div className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
        <div className="w-2 h-2 bg-white rounded-full animate-ping" />
        <span>NOVO</span>
      </div>

      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-50">
        <div className={`text-xs py-1 px-2 rounded whitespace-nowrap ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-black text-white'
          }`}>
          Novo usuário - Status pendente
        </div>
        <div className={`absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent ${isDarkMode ? 'border-t-gray-700' : 'border-t-black'
          }`}></div>
      </div>
    </div>
  );
};

// 🔥 COMPONENTE DO DIALOG ELEGANTE
const VerificationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  userName
}) => {
  const { isDarkMode } = useTheme();

  if (!isOpen) return null;

  const dialogBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = isDarkMode ? 'text-gray-100' : 'text-gray-900';
  const textColorSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-100';
  const infoBg = isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200';
  const infoText = isDarkMode ? 'text-blue-300' : 'text-blue-800';
  const detailBg = isDarkMode ? 'bg-gray-700' : 'bg-gray-50';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl shadow-xl max-w-md w-full transform transition-all duration-300 scale-100 opacity-100 ${dialogBg}`}>
        {/* Header */}
        <div className={`p-6 border-b ${borderColor}`}>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${textColor}`}>
                Verificar Usuário
              </h3>
              <p className={`text-sm mt-1 ${textColorSecondary}`}>
                Confirma a ativação deste usuário?
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className={`border rounded-lg p-4 ${infoBg} ${infoText}`}>
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm">
                <p className="font-medium">Ao verificar este usuário:</p>
                <ul className="mt-2 space-y-1">
                  <li>• Status será alterado para "verificado"</li>
                  <li>• Badge "NOVO" será removido</li>
                  <li>• Usuário terá acesso completo ao sistema</li>
                </ul>
              </div>
            </div>
          </div>

          {userName && (
            <div className={`mt-4 p-3 rounded-lg ${detailBg}`}>
              <p className={`text-sm ${textColorSecondary}`}>
                <span className="font-medium">Usuário:</span> {userName}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex gap-3 p-6 border-t ${borderColor}`}>
          <button
            onClick={onClose}
            className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${isDarkMode
              ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600 focus:ring-gray-500/20'
              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-gray-500/20'
              }`}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500/50 shadow-sm"
          >
            <div className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Confirmar Verificação
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

// 🔥 COMPONENTE PRINCIPAL UsersTable (OTIMIZADO)
const UsersTable = ({
  updateOneSignalUrl,
  toggleUserAdminStatus,
  setSelectedUser,
  setAlertModalOpen,
  filteredUsers = [],
  deleteUser,
}) => {
  const { isDarkMode } = useTheme();

  // 🔥 ESTADOS DE PAGINAÇÃO INTERNA
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(4);

  // 🔥 NOVOS ESTADOS PARA FILTRO
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [appStatusFilter, setAppStatusFilter] = useState('all');

  // 🔥 ESTADOS PARA UI MINIMALISTA
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  // 🔥 ESTADO PARA ARMAZENAR OS PERFIS COMPLETOS DOS USUÁRIOS
  const [userProfiles, setUserProfiles] = useState({});

  // 🔥 BUSCAR DADOS COMPLETOS DE TODOS OS USUÁRIOS DE UMA VEZ
  useEffect(() => {
    const fetchAllUserProfiles = async () => {
      if (!filteredUsers.length) return;

      try {
        const profiles = {};

        // Busca dados completos para cada usuário
        for (const user of filteredUsers) {
          try {
            const userDoc = await getDoc(doc(db, 'users', user.id));
            if (userDoc.exists()) {
              const data = userDoc.data();
              profiles[user.id] = {
                ...data,
                id: user.id,
                fullName: data.fullName || 'Usuário sem nome',
                isNewUser: data.isNewUser || false,
                userStatus: data.userStatus || 'pending',
                isAdmin: data.isAdmin || false,
                phone: data.phone || ''
              };
            }
          } catch (error) {
            console.error(`Erro ao buscar dados do usuário ${user.id}:`, error);
          }
        }

        setUserProfiles(profiles);
      } catch (error) {
        console.error('Erro ao buscar perfis dos usuários:', error);
      }
    };

    fetchAllUserProfiles();
  }, [filteredUsers]); // ✅ Apenas quando filteredUsers mudar

  // 🔥 FUNÇÃO PARA FILTRAR E ORDENAR OS USUÁRIOS
  const getFilteredAndSortedUsers = () => {
    let filtered = filteredUsers || [];

    // Aplicar filtro por status do usuário usando os perfis completos
    if (userStatusFilter === 'new') {
      filtered = filtered.filter(user => {
        const profile = userProfiles[user.id];
        return profile?.isNewUser && profile?.userStatus === 'pending';
      });
    } else if (userStatusFilter === 'verified') {
      filtered = filtered.filter(user => {
        const profile = userProfiles[user.id];
        return profile?.userStatus === 'verified';
      });
    }

    // Aplicar filtro por status do app (exemplo - você pode ajustar conforme seus dados)
    if (appStatusFilter === 'active') {
      filtered = filtered.filter(user =>
        user.isActive !== false // Considera ativo se não for explicitamente false
      );
    } else if (appStatusFilter === 'inactive') {
      filtered = filtered.filter(user =>
        user.isActive === false // Considera inativo apenas se for explicitamente false
      );
    }

    // Aplicar termo de busca local
    if (localSearchTerm.trim() !== '') {
      const term = localSearchTerm.toLowerCase();
      filtered = filtered.filter(user => {
        const profile = userProfiles[user.id] || user;
        return (
          (profile.fullName && profile.fullName.toLowerCase().includes(term)) ||
          (profile.email && profile.email.toLowerCase().includes(term)) ||
          (profile.id && profile.id.toLowerCase().includes(term))
        );
      });
    }

    // Aplicar ordenação por data
    filtered.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);

      return sortOrder === 'newest'
        ? dateB - dateA
        : dateA - dateB;
    });

    return filtered;
  };

  // 🔥 USUÁRIOS FILTRADOS E ORDENADOS
  const processedUsers = getFilteredAndSortedUsers();

  // 🔥 CÁLCULOS DE PAGINAÇÃO
  const safeFilteredUsers = processedUsers;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedItems = safeFilteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(safeFilteredUsers.length / itemsPerPage);

  // 🔥 FUNÇÃO PAGINATE
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // 🔥 RESETAR PAGINAÇÃO QUANDO MUDAR O FILTRO
  useEffect(() => {
    setCurrentPage(1);
  }, [userStatusFilter, sortOrder, appStatusFilter]);

  // 🔥 STATES PARA OS DIALOGS
  const [verificationDialog, setVerificationDialog] = useState({
    isOpen: false,
    userId: null,
    userName: ''
  });



  // 🔥 FUNÇÃO PARA VERIFICAR SE USUÁRIO PRECISA DO BOTÃO "VERIFICAR"
  const needsVerificationButton = (userId) => {
    const profile = userProfiles[userId];
    return profile?.isNewUser && profile?.userStatus === 'pending';
  };

  // Classes condicionais para dark mode
  const containerBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const headerBg = isDarkMode ? 'bg-gray-700' : 'bg-gray-50';
  const textColor = isDarkMode ? 'text-gray-100' : 'text-gray-900';
  const textColorSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const tableHeaderColor = isDarkMode ? 'text-gray-300' : 'text-gray-500';

  const tableBorder = isDarkMode ? 'divide-gray-700' : 'divide-gray-200';
  const hoverBg = isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50/50';
  const cardBg = isDarkMode ? 'bg-gray-700' : 'bg-white';
  const inputBg = isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900';
  const adminBadge = isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800';
  const userBadge = isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-800';
  const messageButtonBg = isDarkMode ? 'bg-green-900/50 text-green-300 hover:bg-green-800/50' : 'bg-green-100 text-green-800 hover:bg-green-200';



  // 🔥 FUNÇÃO PARA ABRIR O DIALOG DE VERIFICAÇÃO
  const openVerificationDialog = (userId, userName) => {
    setVerificationDialog({
      isOpen: true,
      userId,
      userName: userName || 'Usuário sem nome'
    });
  };

  // 🔥 FUNÇÃO PARA FECHAR O DIALOG DE VERIFICAÇÃO
  const closeVerificationDialog = () => {
    setVerificationDialog({
      isOpen: false,
      userId: null,
      userName: ''
    });
  };

  // 🔥 FUNÇÃO PARA MARCAR USUÁRIO COMO VERIFICADO
  const handleMarkAsVerified = async (userId) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isNewUser: false,
        userStatus: 'verified',
        updatedAt: new Date()
      });
      console.log(`✅ Usuário ${userId} marcado como verificado`);

      // Atualiza o estado local
      setUserProfiles(prev => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          isNewUser: false,
          userStatus: 'verified'
        }
      }));

      closeVerificationDialog();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
    }
  };

  return (
    <div>


      {/* 🔥 DIALOG DE VERIFICAÇÃO */}
      <VerificationDialog
        isOpen={verificationDialog.isOpen}
        onClose={closeVerificationDialog}
        onConfirm={() => handleMarkAsVerified(verificationDialog.userId)}
        userName={verificationDialog.userName}
      />

      {/* 🔥 HEADER REDESENHADO - CLEAN & MODERN - Ocultado a pedido do usuário
      <div className="mb-6">
        <div className={`p-5 rounded-xl border shadow-sm ${cardBg} ${borderColor}`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className={`text-xs font-semibold uppercase tracking-wider ${textColorSecondary}`}>
                Status
              </label>
              <div className="relative">
                <select
                  value={userStatusFilter}
                  onChange={(e) => setUserStatusFilter(e.target.value)}
                  className={`w-full appearance-none pl-3 pr-8 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all ${inputBg}`}
                >
                  <option value="all">Todos os usuários</option>
                  <option value="new">Apenas usuários novos</option>
                  <option value="verified">Usuários verificados</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className={`text-xs font-semibold uppercase tracking-wider ${textColorSecondary}`}>
                Ordenação
              </label>
              <div className="relative">
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className={`w-full appearance-none pl-3 pr-8 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all ${inputBg}`}
                >
                  <option value="newest">Mais recentes</option>
                  <option value="oldest">Mais antigos</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className={`text-xs font-semibold uppercase tracking-wider ${textColorSecondary}`}>
                Apps
              </label>
              <div className="relative">
                <select
                  value={appStatusFilter}
                  onChange={(e) => setAppStatusFilter(e.target.value)}
                  className={`w-full appearance-none pl-3 pr-8 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all ${inputBg}`}
                >
                  <option value="all">Todos os status</option>
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      */}

      {/* Header Minimalista: Busca e Filtro (Estilo Neo-Editorial) */}
      <div className="mb-2 flex items-center justify-end gap-3">
        {/* Lupa / Busca Retrátil */}
        <div className={`flex items-center overflow-hidden transition-all duration-300 ease-out border ${isSearchOpen ? 'w-[200px] sm:w-64 opacity-100' : 'w-10 opacity-70 hover:opacity-100'} ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <button 
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className={`p-2.5 flex-shrink-0 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
            title="Buscar usuário"
          >
            <FiSearch size={16} />
          </button>
          
          <input
            type="text"
            placeholder="Buscar por nome, email ou ID..."
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            className={`w-full bg-transparent text-sm focus:outline-none placeholder-gray-500 ${isSearchOpen ? 'px-2' : 'px-0'} ${isDarkMode ? 'text-white' : 'text-black'}`}
          />
          
          {isSearchOpen && localSearchTerm && (
            <button 
              onClick={() => setLocalSearchTerm('')}
              className={`p-2.5 flex-shrink-0 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
            >
              <FiX size={14} />
            </button>
          )}
        </div>

        {/* Menu Dropdown de Filtros */}
        <div className="relative">
          <button
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            className={`p-2.5 border transition-all ${isFiltersOpen || userStatusFilter !== 'all' || appStatusFilter !== 'all' || sortOrder !== 'newest' ? (isDarkMode ? 'border-white text-black bg-white' : 'border-black text-white bg-black') : (isDarkMode ? 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 bg-gray-800' : 'border-gray-200 text-gray-500 hover:text-black hover:border-gray-400 bg-white')}`}
            title="Filtrar"
          >
            <FiFilter size={16} />
          </button>

          {/* Submenu de Filtros */}
          {isFiltersOpen && (
            <div className={`absolute right-0 mt-2 w-[280px] sm:w-64 border shadow-2xl z-20 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`p-3 border-b flex justify-between items-center ${isDarkMode ? 'border-gray-800 bg-black' : 'border-gray-100 bg-gray-50'}`}>
                <span className={`text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Filtros</span>
                <button onClick={() => setIsFiltersOpen(false)} className={`${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}>
                  <FiX size={14} />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Status Usuários */}
                <div className="space-y-2">
                  <label className={`block text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Usuários</label>
                  <select
                    value={userStatusFilter}
                    onChange={(e) => setUserStatusFilter(e.target.value)}
                    className={`w-full appearance-none px-3 py-2 text-sm font-semibold border focus:outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                  >
                    <option value="all">Todos os usuários</option>
                    <option value="new">Apenas novos</option>
                    <option value="verified">Verificados</option>
                  </select>
                </div>
                
                {/* Status App */}
                <div className="space-y-2">
                  <label className={`block text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Status Apps (Atividade)</label>
                  <select
                    value={appStatusFilter}
                    onChange={(e) => setAppStatusFilter(e.target.value)}
                    className={`w-full appearance-none px-3 py-2 text-sm font-semibold border focus:outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                  >
                    <option value="all">Todos os status</option>
                    <option value="active">Ativos</option>
                    <option value="inactive">Inativos</option>
                  </select>
                </div>

                {/* Ordenação */}
                <div className="space-y-2">
                  <label className={`block text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Ordenação</label>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className={`w-full appearance-none px-3 py-2 text-sm font-semibold border focus:outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                  >
                    <option value="newest">Mais recentes primeiro</option>
                    <option value="oldest">Mais antigos primeiro</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabela - Versão Desktop */}
      <div className={`hidden lg:block rounded-2xl shadow-md border-0 overflow-hidden ${containerBg}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={headerBg}>
              <tr>
                <th className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${tableHeaderColor}`}>
                  Usuário
                </th>
                <th className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${tableHeaderColor}`}>
                  Email
                </th>
                <th className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${tableHeaderColor}`}>
                  Contato
                </th>
                <th className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${tableHeaderColor}`}>
                  URL OneSignal
                </th>
                <th className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${tableHeaderColor}`}>
                  Tipo
                </th>
                <th className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${tableHeaderColor}`}>
                  Cadastro
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${tableBorder}`}>
              {paginatedItems.map((user) => (
                <tr key={user.id} className={`transition-colors duration-150 ${hoverBg}`}>
                  {/* Coluna Usuário + Ações (Tags) */}
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className={`text-sm font-medium truncate max-w-[120px] ${textColor}`}>
                            {user.fullName || 'Nome não informado'}
                          </div>
                          <div className={`text-xs font-mono truncate max-w-[120px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                            ID: {user.id.substring(0, 8)}...
                          </div>
                        </div>
                        {/* 🔥 BADGE DO USUÁRIO NOVO - OTIMIZADO */}
                        <NewUserBadge userProfile={userProfiles[user.id]} />
                      </div>

                      {/* Ações como Tags */}
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {/* BOTÃO WHATSAPP */}
                        {user.phone && (
                          <button
                            onClick={() => {
                              const formatted = formatPhoneNumberForWhatsApp(user.phone);
                              if (formatted) {
                                // Usa api.whatsapp.com que é mais robusto
                                window.open(`https://api.whatsapp.com/send?phone=${formatted}`, '_blank');
                              } else {
                                alert('Número inválido para WhatsApp');
                              }
                            }}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors duration-200 border ${messageButtonBg} border-transparent hover:brightness-95`}
                            title="Abrir conversa no WhatsApp"
                          >
                            <span className="inline-flex items-center gap-1"><FaWhatsapp size={10} /> WhatsApp</span>
                          </button>
                        )}

                        {/* BOTÃO ADMIN */}
                        <button
                          onClick={() => toggleUserAdminStatus(user.id, user.isAdmin)}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors border ${user.isAdmin
                            ? isDarkMode
                              ? 'bg-red-900/30 text-red-300 border-red-800/50 hover:bg-red-800/50'
                              : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                            : isDarkMode
                              ? 'bg-green-900/30 text-green-300 border-green-800/50 hover:bg-green-800/50'
                              : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                            }`}
                        >
                          {user.isAdmin ? 'Remover Admin' : 'Tornar Admin'}
                        </button>

                        {/* BOTÃO ALERTA */}
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setAlertModalOpen(true);
                          }}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors border ${isDarkMode
                            ? 'bg-yellow-900/30 text-yellow-300 border-yellow-800/50 hover:bg-yellow-800/50'
                            : 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100'
                            }`}
                          title="Enviar alerta"
                        >
                          <span className="inline-flex items-center gap-1"><FiSend size={10} /> Alerta</span>
                        </button>

                        {/* 🔥 BOTÃO VERIFICAR */}
                        {needsVerificationButton(user.id) && (
                          <button
                            onClick={() => openVerificationDialog(user.id, user.fullName)}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors duration-200 border ${isDarkMode
                              ? 'bg-blue-900/30 text-blue-300 border-blue-800/50 hover:bg-blue-800/50'
                              : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                              }`}
                            title="Marcar como verificado"
                          >
                            <span className="inline-flex items-center gap-1"><FiCheck size={10} /> Verificar</span>
                          </button>
                        )}

                        {/* BOTÃO DELETAR */}
                        {deleteUser && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Tem certeza que deseja deletar o usuário "${user.fullName || user.email}"?\n\n⚠️ ATENÇÃO: O usuário NÃO pode ter aplicativos criados.\nDelete todos os apps do usuário primeiro!\n\nEsta ação NÃO PODE SER DESFEITA!`)) {
                                deleteUser(user.id);
                              }
                            }}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors border ${isDarkMode
                              ? 'bg-red-900/30 text-red-300 border-red-800/50 hover:bg-red-800/50'
                              : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                              }`}
                            title="Excluir usuário permanentemente"
                          >
                            <span className="inline-flex items-center gap-1"><FiTrash2 size={10} /> Excluir</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className={`text-sm font-medium truncate max-w-[150px] ${textColor}`}>
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${textColor}`}>
                      {user.phone || 'Não informado'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="url"
                        value={user.onesignalUrl || ''}
                        onChange={(e) => updateOneSignalUrl(user.id, e.target.value)}
                        placeholder="URL OneSignal"
                        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-colors ${inputBg}`}
                      />
                      {user.onesignalUrl && (
                        <button
                          onClick={() => window.open(user.onesignalUrl, '_blank')}
                          className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-150 ${isDarkMode
                            ? 'bg-indigo-900/50 text-indigo-300 hover:bg-indigo-800/50'
                            : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                            }`}
                          title="Abrir URL"
                        >
                          ↗
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.isAdmin ? adminBadge : userBadge
                        }`}>
                        {user.isAdmin ? 'Admin' : 'User'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        {user.createdAt?.toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </td>
                </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Versão Mobile - OTIMIZADA */}
      <div className="lg:hidden space-y-4">
        {paginatedItems.map((user) => (
          <div key={user.id} className={`rounded-2xl shadow-lg border-0 p-4 ${cardBg}`}>
            <div className="space-y-4">
              {/* Header do Card */}
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className={`text-base font-semibold truncate ${textColor}`}>
                        {user.fullName || 'Nome não informado'}
                      </div>
                      <div className={`text-xs font-mono truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        ID: {user.id.substring(0, 8)}...
                      </div>
                    </div>
                    {/* 🔥 BADGE MOBILE - OTIMIZADO */}
                    <div className="flex-shrink-0">
                      <NewUserBadge userProfile={userProfiles[user.id]} />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.isAdmin ? adminBadge : userBadge
                      }`}>
                      {user.isAdmin ? 'Admin' : 'User'}
                    </span>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                      {user.createdAt?.toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Informações Compactas */}
              <div className="grid grid-cols-1 gap-3 text-sm">
                  {/* Email */}
                  <div className="xs:col-span-1 min-w-0">
                    <div className={`text-xs mb-1 ${textColorSecondary}`}>Email</div>
                    <div className={`truncate text-sm ${textColor}`}>{user.email}</div>
                  </div>
                <div>
                  <div className={`text-xs mb-1 ${textColorSecondary}`}>Contato</div>
                  <div className={textColor}>{user.phone || 'Não informado'}</div>
                </div>
              </div>

              {/* URL OneSignal */}
              <div>
                <div className={`text-xs mb-2 ${textColorSecondary}`}>URL OneSignal</div>
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    value={user.onesignalUrl || ''}
                    onChange={(e) => updateOneSignalUrl(user.id, e.target.value)}
                    placeholder="URL OneSignal"
                    className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 ${inputBg}`}
                  />
                  {user.onesignalUrl && (
                    <button
                      onClick={() => window.open(user.onesignalUrl, '_blank')}
                      className={`p-2 rounded-lg transition-colors ${isDarkMode
                        ? 'bg-indigo-900/50 text-indigo-300 hover:bg-indigo-800/50'
                        : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                        }`}
                      title="Abrir URL"
                    >
                      ↗
                    </button>
                  )}
                </div>
              </div>

              {/* Ações - Layout Melhorado */}
              <div className="grid grid-cols-1 gap-2 pt-2">
                {/* BOTÃO WHATSAPP MOBILE */}
                {user.phone && (
                  <button
                    onClick={() => {
                      const formatted = formatPhoneNumberForWhatsApp(user.phone);
                      if (formatted) {
                        window.open(`https://api.whatsapp.com/send?phone=${formatted}`, '_blank');
                      } else {
                        alert('Número inválido para WhatsApp');
                      }
                    }}
                    className={`w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2 ${messageButtonBg}`}
                    title="Abrir conversa no WhatsApp"
                  >
                    <span className="inline-flex items-center gap-2"><FaWhatsapp /> Enviar WhatsApp</span>
                  </button>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => toggleUserAdminStatus(user.id, user.isAdmin)}
                    className={`px-3 py-2 rounded text-xs font-medium transition-colors ${user.isAdmin
                      ? isDarkMode
                        ? 'bg-red-900/50 text-red-300 hover:bg-red-800/50'
                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                      : isDarkMode
                        ? 'bg-green-900/50 text-green-300 hover:bg-green-800/50'
                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                  >
                    {user.isAdmin ? 'Remover Admin' : 'Tornar Admin'}
                  </button>

                  <button
                    onClick={() => {
                      setSelectedUser(user);
                      setAlertModalOpen(true);
                    }}
                    className={`px-3 py-2 rounded text-xs font-medium hover:bg-yellow-200 whitespace-nowrap transition-colors ${isDarkMode
                      ? 'bg-yellow-900/50 text-yellow-300 hover:bg-yellow-800/50'
                      : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                      }`}
                    title="Enviar alerta"
> 
                    <span className="inline-flex items-center gap-2"><FiSend /> Alerta</span>
                  </button>
                </div>

                {/* 🔥 BOTÃO VERIFICAR MOBILE */}
                {needsVerificationButton(user.id) && (
                  <button
                    onClick={() => openVerificationDialog(user.id, user.fullName)}
                    className={`w-full px-4 py-3 rounded text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2 ${isDarkMode
                      ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-800/50'
                      : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                      }`}
                    title="Marcar como verificado"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Marcar como Verificado
                  </button>
                )}

                {/* BOTÃO DELETAR USUÁRIO MOBILE */}
                {deleteUser && (
                  <button
                    onClick={() => {
                      if (window.confirm(`Tem certeza que deseja deletar o usuário "${user.fullName || user.email}"?\n\n⚠️ ATENÇÃO: O usuário NÃO pode ter aplicativos criados.\nDelete todos os apps do usuário primeiro!\n\nEsta ação NÃO PODE SER DESFEITA!`)) {
                        deleteUser(user.id);
                      }
                    }}
                    className={`w-full px-4 py-3 rounded text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2 ${isDarkMode
                      ? 'bg-red-900/50 text-red-300 hover:bg-red-800/50'
                      : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    title="Excluir usuário permanentemente"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Excluir Usuário
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Paginação */}
      <div className="mt-6">
        <Pagination
          filteredItems={safeFilteredUsers}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          totalPages={totalPages}
          paginate={paginate}
        />
      </div>
    </div>
  );
};

export default UsersTable;